import {
  Button,
  Icon,
  Table,
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  NonCancelableCustomEvent,
  TextFilter,
  Header,
  Box,
} from '@amzn/awsui-components-core';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { GetUserProfileResponse, UserRoles } from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState } from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { tablePaginationAriaLabels } from '../../api/UtilsApi';
import constants from '../../common/constants';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import '../../resources/styles/scss/components/pages/content/AdminConsole.css';
import Breadcrumbs from '../common/Breadcrumbs';
import { DEFAULT_PREFERENCES } from '../common/CommonComponents';
import TableSettingOptions from '../../model/TableSettingOptions';
import NotFound from '../common/NotFound';
import BrowserHistory from '../utils/BrowserHistory';
import { DefaultTablePageSizeOptions } from '../workflow-viewer/ContentWorkflowColumnDefinitions';
import { booleanComparator } from '../utils/Comparator';

const tableColumnsDefinition = [
  {
    cell: (item: GetUserProfileResponse) => `${item.userId}`,
    header: 'UserId',
    id: 'userId',
    sortingField: 'userId',
  },
  {
    cell: (item: GetUserProfileResponse) => `${item.userName}`,
    header: 'Users',
    id: 'userName',
    sortingField: 'userName',
  },
  {
    cell: (item: GetUserProfileResponse) =>
      item.roles.has(UserRoles.Admin) ? <Icon name="status-positive" /> : '',
    header: 'Admin',
    id: 'admin',
    sortingComparator: booleanComparator((item) => item.roles.has(UserRoles.Admin)),
  },
  {
    cell: (item: GetUserProfileResponse) =>
      item.roles.has(UserRoles.Publisher) ? <Icon name="status-positive" /> : '',
    header: 'Publisher',
    id: 'publisher',
    sortingComparator: booleanComparator((item) => item.roles.has(UserRoles.Publisher)),
  },
  {
    cell: (item: GetUserProfileResponse) =>
      item.roles.has(UserRoles.TechWriter) ? <Icon name="status-positive" /> : '',
    header: 'Techwriter',
    id: 'techwriter',
    sortingComparator: booleanComparator((item) => item.roles.has(UserRoles.TechWriter)),
  },
];

const mplService: IMplServiceClient = MplServiceClientFactory.create();

const UserManagement = (props: AppContextProps) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tablePreferences, setTablePreferences] = useState<TableSettingOptions>(
    TableSettingOptions.create(DEFAULT_PREFERENCES),
  );
  // @ts-expect-error TS2339 - strict migration
  const { userProfile } = props.appContext.authnInfo;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      const userProfiles = await mplService.getUserProfiles();
      // @ts-expect-error TS2345 - strict migration
      setUsers(userProfiles);
      setLoadingUsers(false);
    };
    fetchUsers();
  }, [userProfile]);

  const renderEmptyContent = () => {
    return <div> {constants.USER_PERMISSIONS_NO_USERS} </div>;
  };

  const editPermissionPage = () => {
    BrowserHistory.push(`${RouterPaths.ADMIN_EDIT_PERMISSIONS}`);
  };

  const header = (
    <Header
      variant="h3"
      actions={
        <Button variant="primary" onClick={editPermissionPage}>
          {constants.EDIT_USER_PERMISSION_BUTTON_TEXT}
        </Button>
      }
    >
      {constants.USER_PERMISSIONS_MANAGEMENT}
    </Header>
  );

  const setPreferences = (
    e: NonCancelableCustomEvent<CollectionPreferencesProps.Preferences<boolean>>,
  ) => {
    const { pageSize: size, wrapLines } = e.detail;
    setTablePreferences({
      ...tablePreferences,
      pageSize: size ?? constants.TABLE_DEFAULT_PAGE_SIZE,
      wrapLines: wrapLines ?? false,
    });
  };

  const { pageSize, wrapLines } = tablePreferences;

  const preferences: CollectionPreferencesProps.Preferences<boolean> = {
    pageSize,
    wrapLines,
  };

  const { items, collectionProps, paginationProps, filterProps } = useCollection(users, {
    filtering: {
      empty: 'No contents',
      noMatch: constants.NO_RESULT_FOUND,
    },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: 'id' },
        isDescending: true,
      },
    },
    pagination: { pageSize: preferences.pageSize },
  });

  const renderUserTable = (userProfiles: any) => {
    const collectionPreferences = (
      <CollectionPreferences
        title={constants.PREFERENCES}
        confirmLabel={constants.CONFIRM_BUTTON_TEXT}
        cancelLabel={constants.CANCEL_BUTTON_TEXT}
        preferences={preferences}
        pageSizePreference={{
          title: constants.TABLE_PAGE_SELECTOR_MESSAGE,
          options: DefaultTablePageSizeOptions,
        }}
        wrapLinesPreference={{
          label: constants.TABLE_WRAPLINES_LABEL,
          description: constants.TABLE_WRAPLINES_DESCRIPTION,
        }}
        onConfirm={setPreferences}
      />
    );

    return (
      // @ts-expect-error TS2322 - strict migration
      <Table
        {...collectionProps}
        loading={loadingUsers}
        header={header}
        empty={renderEmptyContent()}
        columnDefinitions={tableColumnsDefinition}
        resizableColumns
        wrapLines
        items={items}
        preferences={collectionPreferences}
        pagination={<Pagination {...paginationProps} ariaLabels={tablePaginationAriaLabels} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder={constants.USER_PERMISSION_MGMT_USER_SEARCH_LABEL}
            filteringAriaLabel="Filter users"
          />
        }
      />
    );
  };

  if (userProfile.roles.has(UserRoles.Admin)) {
    const breadcrumbItems = [
      { text: constants.APP_NAME_BREAD_CRUMB, href: '/' },
      { text: constants.PERMISSIONS, href: '' },
    ];
    return (
      <div>
        <Breadcrumbs items={breadcrumbItems} />
        <Box padding={{ top: 'm' }}>{renderUserTable(users)}</Box>
      </div>
    );
  }

  return <NotFound />;
};

export default withAppContext(UserManagement);
